/* eslint-disable react/prop-types */
import { useContext, useEffect, useState } from "react";
import { Text } from "flicket-ui";
import { useRouter } from "next/router";
import { Layout, Seo, Status } from "~components";
import {
  Header,
  NonSeated as NonSeatedComponent,
} from "~components/reservation";
import { BaseContext } from "~context";
import { useAccount, useNonSeatedReservation } from "~hooks";
import { getEvent, getRelease, Release } from "~graphql/fetchers";
import { getError, getUrl, showToast } from "~lib";

import useSWR from "swr";
import { AccessModal } from "~components/releases";
import { TicketDetailsModal } from "~features/seated-reservation/components/common/modal.TicketDetails";
import { Seated as SeatedReservation } from "~features/seated-reservation/reservation.Seated";
import * as actions from "~features/seated-reservation/store/actions/index";
import { QueryParams } from "~features/seated-reservation/types";
import { EventKind, EventQuery, VenueSeating } from "~graphql/sdk";
import omit from "lodash/omit";
import HeaderAlert from "~components/common/common.HeaderAlert";
import { useCampaignReportingOpen } from "~hooks/useBroadastCampaignReporting";
import { useReferralCode } from "~hooks/useReferralCode";
import { useGoogleAnalyticsPageView } from "~hooks/useGoogleAnalytics";
import { GetServerSideProps, NextPage } from "next";
import {
  CompetitionSeoProps,
  competitionSeo,
  asOpenGraphImage,
} from "~features/competitions/competitionSeo";
import { store } from "~lib/store";

type Query = {
  eventId?: string;
  release?: string;
  slug?: string;
  ownedTickets?: string;
  session?: string;
  posId?: string;
  customer?: string;
  zone?: string;
};

const NoAccessAlert = () => {
  return (
    <Text
      py="1/2"
      px={2}
      bg="S300"
      color="white"
      textAlign="center"
      fontWeight="extraBold"
      w="100%"
      zIndex={1000}
    >
      Note: This release is not accessible to the public
    </Text>
  );
};

export const getServerSideProps: GetServerSideProps<CompetitionSeoProps> = async (
  context
) => {
  return await competitionSeo(
    store.organization.id,
    context.params?.eventId as string
  );
};

const NonSeatedReservation = ({
  type,
  typeId,
  isDigital,
}: {
  type: "event" | "membership";
  typeId: string;
  isDigital: boolean;
}) => {
  const router = useRouter();
  const { isAdmin } = useAccount();
  const query = router.query as Query;

  const {
    event,
    handleGAOrderCreation,
    selectableZones,
    release,
  } = useNonSeatedReservation({
    type,
    id: typeId,
    releaseId: query.release,
    slug: query.slug,
  });

  const onlyShowZone = !!query?.zone;
  return (
    <NonSeatedComponent
      isAdmin={isAdmin}
      isDigital={isDigital}
      zones={
        onlyShowZone
          ? selectableZones.filter(
              (item) => item.name === decodeURIComponent(query.zone)
            )
          : selectableZones
      }
      addons={release?.releaseEventAddons.map(({ eventAddon }) => eventAddon)}
      eventOrMembership={event}
      createOrder={handleGAOrderCreation}
      releaseNotes={release?.releaseNotes}
      allowPublicComp={release?.allowPublicComp}
    />
  );
};

const Reservation = ({ event }: { event: EventQuery["event"] }) => {
  const router = useRouter();
  const query = router.query as Query;
  const { organization } = useContext(BaseContext);
  const { isAdmin, isInitialising } = useAccount();
  const [hasAccess, setHasAccess] = useState<boolean | undefined>(undefined);

  // Store referralCode & version in sessionStorage.
  useReferralCode();

  const { data: release, error: releaseError } = useSWR<Release, Error>(
    ["release", organization?.id, event.id, query.release, query.slug],
    getRelease
  );

  const isLoading = (!release && !releaseError) || isInitialising;

  const [triggerCampaignReporting, setTriggerCampaignReporting] = useState(
    false
  );

  useCampaignReportingOpen(triggerCampaignReporting);

  useEffect(() => {
    void router.prefetch("/checkout/[orderId]");
    void router.prefetch("/events/[eventId]");
  }, []);

  useEffect(() => {
    if (router.query?.session !== QueryParams.SessionContinue) {
      actions.resetState();
    }
  }, []);

  /**
   * This effect handles all of the redirects back to the info page, making sure
   * the the query params stay the same.
   */
  useEffect(() => {
    if (isLoading) {
      return;
    }
    let redirect = false;

    // Take all params except the `eventId` because this is part of the url
    const queryParams: Query = omit(query, "eventId");

    const canViewInactiveRelease: boolean = isAdmin || !!query.posId;

    if (releaseError) {
      if (query.posId) {
        showToast(getError(releaseError, "graphQL"), "error");
        return window.location.reload();
      }
      showToast(getError(releaseError, "graphQL"), "error");
      redirect = true;
    } else if (
      release?.isActive === false &&
      canViewInactiveRelease === false
    ) {
      redirect = true;
      showToast("This release is not publicly available", "error");
    }

    if (redirect) {
      void router.push({ pathname: `/events/${event.id}`, query: queryParams });
    } else {
      setTriggerCampaignReporting(true);
    }
  }, [event.id, release, releaseError, isAdmin, isLoading]);

  useEffect(() => {
    if (release?.requiresAuth !== undefined) {
      setHasAccess(!release.requiresAuth);
    }
  }, [release?.requiresAuth]);

  return (
    <>
      <Header
        title={event.title}
        startDate={event.startDate}
        endDate={event.endDate}
        location={
          event.venue?.name &&
          `${event.venue?.name}, ${event.venue?.address?.city}`
        }
        goBack={() => {
          if (query.posId) {
            window.location.href = `${getUrl(organization.slug)}/pos/${
              query.posId
            }`;
          } else {
            void router.push("/");
          }
        }}
        accessibility={event.accessibility}
        supportInfo={organization?.supportInfo}
        image={event.logo}
        description={event.description}
        coordinates={`${event.venue?.location?.lat},${event.venue?.location?.lng}`}
      />
      <Layout layoutType="sub">
        {isAdmin && release?.isActive === false && <NoAccessAlert />}
        {isAdmin && (
          <HeaderAlert
            message="Back office order"
            fixed={event.venue?.seating !== VenueSeating.Seated}
          />
        )}
        <Status error={releaseError} loading={isLoading}>
          {!isLoading && (isAdmin || (release?.isActive && hasAccess)) && (
            <>
              {event.venue?.seating !== VenueSeating.Seated ? (
                <NonSeatedReservation
                  type="event"
                  typeId={query.eventId}
                  isDigital={event.eventKind === EventKind.Digital}
                />
              ) : (
                <SeatedReservation
                  type="event"
                  typeId={query.eventId}
                  release={release}
                  defaultShowOptions={!query.ownedTickets && !query.session}
                />
              )}
              <TicketDetailsModal />
            </>
          )}
          {/* {release?.requiresAuth && !isAdmin && ( */}
          <AccessModal
            hasAccess={hasAccess}
            setHasAccess={setHasAccess}
            release={release}
          />
          {/* )} */}
        </Status>
      </Layout>
    </>
  );
};

const ReservationPage: NextPage<CompetitionSeoProps> = ({
  activeCompetition: { data: activeCompetition },
  eventImageUrls: { data: eventImageUrls },
  eventTitle: { data: eventTitle },
}) => {
  const router = useRouter();
  const { organization } = useContext(BaseContext);
  const { eventId } = router.query as Query;
  useGoogleAnalyticsPageView(eventId);

  const { error, data: event } = useSWR(
    ["event", organization.id, eventId],
    getEvent
  );

  const isLoading = !event && !error;
  const isInactive = event?.isActive === false;

  const title = activeCompetition
    ? activeCompetition.title
    : "Get your tickets now!";

  useEffect(() => {
    if (isInactive) {
      void router.push("/");
    }
  }, [isInactive]);

  return (
    <>
      <Seo
        title="Tickets"
        description="Tickets"
        options={{
          openGraph: {
            title,
            description: eventTitle,
            images: eventImageUrls?.map((url) =>
              asOpenGraphImage(url, eventTitle)
            ),
          },
        }}
      />
      <Status
        error={error}
        loading={isLoading || !event?.venue?.seating || isInactive}
      >
        <Reservation event={event} />
      </Status>
    </>
  );
};

export default ReservationPage;
